import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { k8sResourceService } from '@/services/k8sResourceService'
import { authService } from '@/services/authService'
import { projectService, projectLabel } from '@/services/projectService'
import ProjectLayout from '@/components/ProjectLayout'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import FlowCanvas from '@/components/flow/FlowCanvas'
import { ExternalLink, StatusLabel, type StatusType } from '@/components/ui/labels'
import { HttpError } from '@/lib/httpError'
import UpdateFlowConfirmModal, { type UpdateFlowConfirmModalRef } from '@/components/modals/UpdateFlowConfirmModal'
import ImageConfigEditor from '@/components/project-flow/ImageConfigEditor'
import GitConfigEditor from '@/components/project-flow/GitConfigEditor'
import IngressConfigEditor from '@/components/project-flow/IngressConfigEditor'
import StorageConfigEditor from '@/components/project-flow/StorageConfigEditor'
import ServiceConfigEditor from '@/components/project-flow/ServiceConfigEditor'
import EnvConfigEditor from '@/components/project-flow/EnvConfigEditor'
import SecurityContextEditor from '@/components/project-flow/SecurityContextEditor'
import ResourcesConfigEditor from '@/components/project-flow/ResourcesConfigEditor'
import { dateLocaleFor } from '@/lib/dateLocale'

// Node-RED flow interfaces
interface NodeRedNode {
  id: string
  type: string
  name?: string
  label?: string
  x?: number
  y?: number
  z?: string
  wires?: string[][]
  [key: string]: any
}

interface NodeRedFlow {
  id: string
  label?: string
  disabled?: boolean
  nodes: NodeRedNode[]
  configs: NodeRedNode[]
}

// Get default Node-RED URL
// Get Node-RED access URL from flow
// Get External Access URL (using externalIP + port)
const getExternalAccessUrl = (flow: any) => {
  if (!flow.status?.externalIP || !flow.status?.port) return null
  const token = authService.getAccessToken()
  const baseUrl = `http://${flow.status.externalIP}:${flow.status.port}`
  return token ? `${baseUrl}?access_token=${token}` : baseUrl
}

// Get NodePort Access URL (using random node IP + nodePort)
const getNodePortUrl = (flow: any, nodeIPs: string[]) => {
  if (!flow.status?.nodePort || !nodeIPs.length) return null
  // Randomly select a node IP
  const randomNodeIP = nodeIPs[Math.floor(Math.random() * nodeIPs.length)]
  const token = authService.getAccessToken()
  const baseUrl = `http://${randomNodeIP}:${flow.status.nodePort}`
  return token ? `${baseUrl}?access_token=${token}` : baseUrl
}

// Get Ingress URL (protocol based on TLS)
const getIngressUrl = (flow: any) => {
  if (!flow.status?.ingressHost) return null
  const protocol = flow.status.tls ? 'https' : 'http'
  const token = authService.getAccessToken()
  const baseUrl = `${protocol}://${flow.status.ingressHost}`
  return token ? `${baseUrl}?access_token=${token}` : baseUrl
}

export default function ProjectPipelineStudioDetailPage() {
  const { t, i18n } = useTranslation(['pipeline-studio', 'common'])
  const { user, refreshAuth } = useAuth()
  const { projectId, namespace, name } = useParams<{ projectId: string; namespace: string; name: string }>()
  const navigate = useNavigate()
  const updateModalRef = useRef<UpdateFlowConfirmModalRef>(null)

  const [project, setProject] = useState<any>(null)
  const [flow, setFlow] = useState<any | null>(null)
  const [nodeRedFlows, setNodeRedFlows] = useState<NodeRedFlow[]>([])
  const [nodeIPs, setNodeIPs] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'overview' | 'editor' | 'settings'>('overview')
  const [activeFlowIndex, setActiveFlowIndex] = useState(0)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editedDescription, setEditedDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Check permissions
  // One of our own accounts, not the platform role: every client admin is a
  // platform owner, so the role would hand each client the others' projects.
  const isSuperuser = user?.superuser === true
  const isProjectOwner = project?.role === 'owner'
  const isProjectAdmin = project?.role === 'admin'

  // View permissions (BAFTT-16)
  //
  // Any project member may read what a pipeline is and how it is doing. What
  // belongs to the owner alone is reaching the running editor, so the gate sits
  // on the editor tab and on the addresses that lead to it, not on the page.
  //
  // Those addresses answer on the flow itself, which is a way into the editor
  // that never passes through this console. They stay visible, because knowing
  // where something runs is part of knowing its state, but they do not open.
  // The viewer draws the flow from what the server read for us, so every member
  // may open that tab. Reaching the editor itself stays the owner's.
  const canReachEditor = isProjectOwner

  // Update permissions
  const canShowUpdateButtons = isSuperuser || isProjectOwner || isProjectAdmin
  const canDirectUpdate = isSuperuser || isProjectOwner
  const canRequestUpdate = isSuperuser || isProjectOwner || isProjectAdmin

  // Fetch project info
  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!projectId) return

      try {
        const data = await projectService.getProjectById(projectId)
        setProject(data)
        return
      } catch (err) {
        let failure = err
        if (err instanceof HttpError && err.status === 401) {
          try {
            await refreshAuth()
            const data = await projectService.getProjectById(projectId)
            setProject(data)
            return
          } catch (refreshError) {
            failure = refreshError
          }
        }

        // Without the project there is no namespace to query, so the effect that
        // loads the flow never runs and the initial loading state would stand for
        // good. Say what went wrong rather than spin.
        console.error('Failed to fetch project info:', failure)
        const message = failure instanceof Error ? failure.message : 'errors.operationFailed'
        setError(message.startsWith('errors.') ? t(message, { ns: 'common', defaultValue: message }) : message)
        setLoading(false)
      }
    }

    fetchProjectInfo()
  }, [projectId, refreshAuth])

  // Helper function to process and set flows from Node-RED response
  const processAndSetFlows = (flows: NodeRedNode[]) => {
    const flowsMap = new Map<string, NodeRedFlow>()
    const tabNodes: NodeRedNode[] = []

    // First pass: collect all tab definitions
    flows.forEach(node => {
      if (node.type === 'tab') {
        tabNodes.push(node)
        flowsMap.set(node.id, {
          id: node.id,
          label: node.label || node.id,
          disabled: node.disabled || false,
          nodes: [],
          configs: []
        })
      }
    })

    // Second pass: assign nodes to their tabs
    flows.forEach(node => {
      if (node.type !== 'tab' && node.z) {
        const flowId = node.z
        if (flowsMap.has(flowId)) {
          const flow = flowsMap.get(flowId)!
          if (!node.x || !node.y) {
            flow.configs.push(node)
          } else {
            flow.nodes.push(node)
          }
        }
      }
    })

    const flowsArray = Array.from(flowsMap.values())
    setNodeRedFlows(flowsArray)

    // Set first non-disabled flow as active
    const firstActiveIndex = flowsArray.findIndex(f => !f.disabled)
    if (firstActiveIndex >= 0) {
      setActiveFlowIndex(firstActiveIndex)
    }

    return true
  }

  useEffect(() => {
    // Wait for project to load before checking permission
    if (!project) return

    if (namespace && name) {
      fetchFlowData()
    }
  }, [namespace, name, project])

  const fetchFlowData = async () => {
    if (!namespace || !name) return

    setLoading(true)
    setError('')
    const startTime = Date.now()

    try {
      // Fetch flow details and nodes in parallel
      const [flowData, nodesData] = await Promise.all([
        k8sResourceService.getFlow(namespace, name),
        k8sResourceService.getNodes()
      ])

      setFlow(flowData)

      // Extract node IPs
      const ips = k8sResourceService.extractNodeIPs(nodesData)
      setNodeIPs(ips)

      // Read the diagram through the console, not from the editor directly
      // (BAFTT-16). The editor only admits the project owner, so asking it from
      // the browser left every other member looking at an empty canvas. The
      // server reads it on their behalf and returns the shape alone.
      try {
        const graph = await k8sResourceService.getFlowGraph(namespace, name)
        processAndSetFlows(graph.nodes || [])
      } catch (graphError) {
        console.error('Failed to read the pipeline diagram:', graphError)
        setNodeRedFlows([])
      }

      // Ensure minimum 2 seconds loading time
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }
    } catch (err) {
      // Handle 401 error
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          return fetchFlowData()
        } catch (refreshError) {
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, 2000 - elapsed)
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }
          const refreshErrorMessage = refreshError instanceof Error ? refreshError.message : 'errors.sessionExpired'
          if (refreshErrorMessage.startsWith('errors.')) {
            setError(t(refreshErrorMessage, { ns: 'common', defaultValue: refreshErrorMessage }))
          } else {
            setError(refreshErrorMessage)
          }
          setLoading(false)
          return
        }
      }

      // For other errors
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = dateLocaleFor(i18n.language)
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    } catch {
      return dateString
    }
  }

  const getAuthor = () => {
    return flow?.metadata?.annotations?.['synstream.console/author'] || '-'
  }

  const getDescription = () => {
    return flow?.metadata?.annotations?.['synstream.console/description'] || '-'
  }

  const handleEditDescription = () => {
    setEditedDescription(getDescription() === '-' ? '' : getDescription())
    setIsEditingDescription(true)
  }

  const handleCancelEdit = () => {
    setIsEditingDescription(false)
    setEditedDescription('')
  }

  const handleSaveDescription = async () => {
    if (!flow) return

    setIsSaving(true)
    try {
      // Create updated flow object
      const updatedFlow = {
        ...flow,
        metadata: {
          ...flow.metadata,
          annotations: {
            ...flow.metadata?.annotations,
            'synstream.console/description': editedDescription.trim() || undefined
          }
        }
      }

      // Remove undefined annotation if description is empty
      if (!editedDescription.trim()) {
        delete updatedFlow.metadata.annotations['synstream.console/description']
      }

      // Open update confirmation modal
      updateModalRef.current?.open({
        name: flow.metadata.name,
        namespace: flow.metadata.namespace,
        manifest: updatedFlow,
        configName: t('detail.description')
      })

      setIsEditingDescription(false)
      setEditedDescription('')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update description'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Handle config editor save - opens update confirmation modal
  const handleConfigSave = (updatedManifest: any, configName: string) => {
    updateModalRef.current?.open({
      name: flow.metadata.name,
      namespace: flow.metadata.namespace,
      manifest: updatedManifest,
      configName
    })
  }

  // Show loading or error
  if (loading || error) {
    return (
      <ProjectLayout>
        <div className="absolute inset-0">
          {loading && !error && <LoadingOverlay message={t('projectLoading', 'Loading flow details...')} fullScreen />}
          {error && !loading && (
            <ErrorDisplay
              error={error}
              title={t('errors.title', 'Error')}
              onRefresh={() => fetchFlowData()}
              fullScreen
            />
          )}
        </div>
      </ProjectLayout>
    )
  }

  if (!flow) {
    return (
      <ProjectLayout>
        <div className="absolute inset-0">
          <ErrorDisplay
            error={t('errors.notFound', 'Flow not found')}
            title={t('errors.title', 'Error')}
            onRefresh={() => navigate(`/projects/${projectId}/products/pipeline-studio`)}
            fullScreen
          />
        </div>
      </ProjectLayout>
    )
  }

  return (
    <ProjectLayout>
      <div className="absolute inset-0 flex flex-col">
        {/* Header */}
        <div className="flex-none bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/projects/${projectId}/products/pipeline-studio`)}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-text-dark">{flow.metadata?.name}</h1>
                <p className="text-sm text-gray-500">{project ? projectLabel(project) : flow.metadata?.namespace}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchFlowData()}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{t('actions.refresh', 'Refresh')}</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'overview'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('detail.overview', 'Overview')}
            </button>
            {(
              <button
                onClick={() => setActiveTab('editor')}
                className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                  activeTab === 'editor'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('detail.editor', 'Flow Viewer')}
              </button>
            )}
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                activeTab === 'settings'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('detail.settings', 'Settings')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-full overflow-auto">
          {activeTab === 'overview' && (
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-text-dark mb-4">{t('detail.basicInfo', 'Basic Information')}</h2>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('flow.name', 'Name')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{flow.metadata?.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('flow.namespace', 'Namespace')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{flow.metadata?.namespace}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('flow.status', 'Status')}</dt>
                    <dd className="mt-1">
                      <StatusLabel status={(flow.status?.phase || 'Pending') as StatusType} size="sm" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('flow.author', 'Author')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{getAuthor()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('flow.created', 'Created')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(flow.metadata?.creationTimestamp)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('flow.replicas', 'Replicas')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{flow.spec?.replicas || 1}</dd>
                  </div>
                </dl>
              </div>

              {/* Description */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-text-dark">{t('detail.description', 'Description')}</h2>
                  {canShowUpdateButtons && !isEditingDescription && (
                    <button
                      onClick={handleEditDescription}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {t('detail.edit', 'Edit')}
                    </button>
                  )}
                </div>

                {isEditingDescription ? (
                  <div>
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                      placeholder={t('detail.descriptionPlaceholder', 'Enter description...')}
                      disabled={isSaving}
                    />
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={handleSaveDescription}
                        disabled={isSaving}
                        className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        {isSaving ? (
                          <>
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('detail.saving', 'Saving...')}
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {t('detail.save', 'Save')}
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        {t('cancel', { ns: 'common' })}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{getDescription()}</p>
                )}
              </div>

              {/* Access URLs */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-text-dark mb-4">{t('detail.accessUrls', 'Access URLs')}</h2>
                <div className="space-y-3">
                  {/* Ingress Access */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">{t('flow.ingressAccess', 'Ingress Access')}:</span>
                    <ExternalLink href={getIngressUrl(flow)} disabled={!canReachEditor} notice={t('restricted.link', 'Only the project owner can open a pipeline')} variant="primary" size="sm">
                      {flow.status?.ingressHost || '-'}
                    </ExternalLink>
                  </div>

                  {/* External Access */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">{t('flow.externalAccess', 'External Access')}:</span>
                    <ExternalLink href={getExternalAccessUrl(flow)} disabled={!canReachEditor} notice={t('restricted.link', 'Only the project owner can open a pipeline')} variant="success" size="sm">
                      {flow.status?.externalIP || '-'}
                    </ExternalLink>
                  </div>

                  {/* NodePort Access */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">{t('flow.nodePortAccess', 'NodePort Access')}:</span>
                    <ExternalLink href={getNodePortUrl(flow, nodeIPs)} disabled={!canReachEditor} notice={t('restricted.link', 'Only the project owner can open a pipeline')} variant="warning" size="sm">
                      {flow.status?.nodePort || '-'}
                    </ExternalLink>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'editor' && (
            <div className="h-full flex flex-col p-6">
              {nodeRedFlows.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <p className="text-lg">{t('detail.noFlows', 'No flows available')}</p>
                    <p className="text-sm mt-2">{t('detail.noFlowsDesc', 'Unable to connect to Node-RED or no flows configured')}</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Flow tabs */}
                  {nodeRedFlows.length > 1 && (
                    <div className="flex-none bg-white border-b border-gray-200 px-4 py-2 flex gap-2 overflow-x-auto mb-4 rounded-t-lg">
                      {nodeRedFlows.map((nrFlow, index) => (
                        <button
                          key={nrFlow.id}
                          onClick={() => setActiveFlowIndex(index)}
                          disabled={nrFlow.disabled}
                          className={`px-4 py-2 text-sm rounded transition-colors whitespace-nowrap ${
                            activeFlowIndex === index
                              ? 'bg-primary text-white'
                              : nrFlow.disabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {nrFlow.label || `Flow ${index + 1}`}
                          {nrFlow.disabled && ' (Disabled)'}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* ReactFlow canvas */}
                  <div className="flex-1 bg-gray-50 rounded-lg overflow-hidden">
                    <FlowCanvas nodes={nodeRedFlows[activeFlowIndex]?.nodes || []} />
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
              {/* Image Configuration */}
              <ImageConfigEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Service Configuration */}
              <ServiceConfigEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Storage Configuration */}
              <StorageConfigEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Git Configuration */}
              <GitConfigEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Ingress Configuration */}
              <IngressConfigEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Environment Variables Configuration */}
              <EnvConfigEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Security Context Configuration */}
              <SecurityContextEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Resources Configuration */}
              <ResourcesConfigEditor
                flow={flow}
                canEdit={canShowUpdateButtons}
                onSave={handleConfigSave}
              />

              {/* Timezone Configuration (Read-only) */}
              {flow.spec?.timezone && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold text-text-dark mb-4">{t('detail.timezoneConfig', 'Timezone Configuration')}</h2>
                  <dl className="grid grid-cols-1 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('detail.timezone', 'Timezone')}</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{flow.spec.timezone}</dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Update Flow Confirm Modal */}
        <UpdateFlowConfirmModal
          ref={updateModalRef}
          canDirectUpdate={canDirectUpdate}
          canRequestUpdate={canRequestUpdate}
          onSuccess={() => fetchFlowData()}
        />
      </div>
    </ProjectLayout>
  )
}
